<template>
	<section :class="sectionClasses">
		<div class="container">
			<div class="col-8-md col-offset-2-md">
				<h2 :class="['overline mb-100', { 'text-color-gray-20': isDarkTheme }]">About our strategy</h2>
				<h3 class="heading-1"> Market dislocation creates opportunity</h3>
				<p class="mt-150-smo"
					>The opportunities created by the Great Deleveraging are unique in that nearly every borrower and
					every asset (regardless of credit quality) are impacted, hence the name “Great.” No matter who you
					are, if you were active in business over the past several years then you were inevitably borrowing
					to some extent. And if you were borrowing at all, then you were borrowing at low rates and
					relatively high asset values.
				</p>
				<p>
					Now that the environment has shifted, regardless of the quality of the underlying asset, as loans
					mature and come due, there will be a gap created during the refinancing period where new equity
					capital must come in to pay down the overall size of the loan.
				</p>

				<h4 class="heading-5 mt-150">Funding the gap</h4>
				<p
					>Our strategy is to focus on bridging the funding gap and providing rescue capital to borrowers in
					the midst of the liquidity crunch. By lending into the gap, we are able to invest at a healthy
					margin of safety, concentrating on high-quality assets with creditworthy borrowers—those who are
					experiencing circumstantial liquidity needs as a result of interest rates rising so rapidly through
					2022 and 2023.</p
				>
				<p>
					In these instances, the underlying assets themselves are typically unaffected by the financial
					turbulence happening in capital markets. Most frequently, the borrower is in the middle of a
					business plan to enhance the value of the property, such as new construction, renovations, or
					lease-up, and simply needs more time to reach stabilization and be ready for long-term, fixed-rate
					debt.</p
				>
				<h5 :class="['overline mt-300', { 'text-color-gray-20': isDarkTheme }]">
					Examples of this kind of activity include:
				</h5>
				<ul class="ul col-9-md">
					<li
						>Originating and structuring real estate loans, including senior mortgage loans and subordinated
						mortgage loans</li
					>
					<li class="mt-100"
						>Providing mezzanine financing in the form of preferred equity, B-notes, or second trusts</li
					>
					<li class="mt-100"
						>Sizing the mezzanine or preferred loans to a GSE (e.g. Fannie Mae or Freddie Mac) exit</li
					>
					<li class="mt-100">Financing residential construction and development</li>
					<li class="mt-100"
						>Acquiring subordinate notes and high-yield investments in the asset backed securities
						market,—single family rental portfolios, in particular</li
					>
				</ul>

				<h4 class="heading-5 mt-300">Focusing on the markets we know best</h4>
				<p
					>In terms of location, we will primarily target high-growth markets in the Sunbelt like Dallas-Fort
					Worth, Phoenix, Orlando, Tampa, Houston, Atlanta, Charleston, and Las Vegas. These are the markets
					we know the best: approximately 70% of all Fundrise acquisitions from 2021-2022 were in the four
					fastest-growing states—Texas, Florida, North Carolina, and Georgia—and more than 90% were within the
					Sunbelt.</p
				>
				<p>
					We believe that by maintaining rigorous credit underwriting with a heavy emphasis on residential
					rental properties,
					<strong
						>we have the opportunity to achieve some of the best relative risk-adjusted returns since the
						aftermath of the Great Recession in 2008.</strong
					></p
				>
				<img class="mt-300" :src="chartImageUrl" alt="" role="presentation" />
			</div>

			<InfoCardsInverted v-if="props.showExampleProject" :cards="cards" :theme="props.theme" />
		</div>
	</section>
</template>

<script setup lang="ts">
import { appBasePath, appCloudfrontPath } from '@constants';
import { computed } from 'vue';
import { InfoCard } from 'types/layout';
import InfoCardsInverted from '@components/layout/info-cards-inverted.vue';
import { useAppStore } from '@stores/app';

interface Props {
	theme?: 'light' | 'dark';
	showExampleProject?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	theme: 'dark',
	showExampleProject: true
});

const appStore = useAppStore();

const assetLink = `<a href="${appBasePath}/assets" class="link text-color-current-color text-decoration-underline" target="_blank">Fundrise Portfolio</a>`;

const cards: Array<InfoCard> = [
	{
		heading: 'Example project',
		title: 'Waypoint',
		description: [
			`As part of our new private credit strategy, we’ve invested roughly $20.8 million to provide financing in the form of preferred equity for the development of the Mason at Daytona Beach, a 300-unit multifamily community on 65.4 acres of centrally located land in Daytona Beach, Florida. Under the terms of the investment agreement, the borrower has agreed to pay us a 13.5% fixed annual rate that will accrue for as long as it takes to finish the project, and our investment will be paid back upon its completion. This is one of many projects in the ${assetLink}.`
		],
		link: {
			text: 'Read the full update',
			router: 'investor-update',
			params: { investorUpdateId: '926' },
			target: '_blank'
		},
		imageUrl: '/images/marketing-site/waypoint-spotlight.png',
		imageAlt: 'Villa with a pool and pine trees'
	}
];

const isDarkTheme = computed((): boolean => props.theme === 'dark');

const sectionClasses = computed((): string => {
	const colors = isDarkTheme.value ? 'bg-color-content-primary text-color-white' : 'bg-color-white';
	return `marketing-section ${colors}`;
});

const chartImageUrl = computed(() => {
	return appStore.isMobile
		? `${appCloudfrontPath}/images/marketing-site/chart-great-deleveraging-mobile.png`
		: `${appCloudfrontPath}/images/marketing-site/chart-great-deleveraging.png`;
});
</script>
